import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceSynapse } from '@mirai/services';
import { Action, Modal, ScrollView, styles, Text, useDevice } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { BANNED_COMPONENTS, DELAY_RESPONSE, INPUT_MODE } from './Chat.constants';
import { L10N } from './Chat.l10n';
import * as style from './Chat.module.css';
import { ButtonDispatcher, Header, Input, Message, Rating, Welcome } from './components';
import { Aura } from './components/Aura/Aura';
import { audioNotification, ClientLobby } from './helpers';
import { getChannelSettings } from './services/lobby.service';
import { SARAI_SOURCE, TYPE } from '../../Core.constants';
import { EVENT, getPrivacyPolicy, METRICS_EVENTS } from '../helpers';

let client;

const Chat = ({ hotelId, ...others }) => {
  const { translate } = useLocale();
  const { isMobile } = useDevice();
  const {
    value: {
      calendar,
      components = {},
      currency,
      hotel = {},
      id,
      language,
      locale,
      occupation,
      origin,
      session,
      skeleton = false,
      type,
      urlParams = {},
    } = {},
  } = useStore();

  const [busy, setBusy] = useState(false);
  const [channelSettings, setChannelSettings] = useState();
  const [inputMode, setInputMode] = useState();
  const [messages, setMessages] = useState([]);
  const [visible, setVisible] = useState(false);
  const activeHotelId = hotelId || hotel.id;

  useEffect(() => {
    if (skeleton) return;

    Event.subscribe(EVENT.CHAT_OPEN, handleOpen);
    return () => Event.unsubscribe(EVENT.CHAT_OPEN, handleOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skeleton]);

  useEffect(() => {
    if (skeleton || (!id && !hotelId)) {
      setChannelSettings();
      return;
    }

    let active = true;

    const loadSettings = async () => {
      const channel = await getChannelSettings({ channelId: hotelId || id });
      if (!active) return;
      setChannelSettings(channel?.settings);
    };

    loadSettings();

    return () => {
      active = false;
    };
  }, [hotelId, id, skeleton]);

  useEffect(() => {
    const keepAlive = visible && (inputMode === INPUT_MODE.TEXT || inputMode === INPUT_MODE.AUDIO);
    if (!keepAlive) return client?.disconnect();

    const channelId = activeHotelId || id;
    if (client?.socket && client.channelId === channelId && client.session === session) return;
    client?.disconnect();

    client = new ClientLobby({ channelId, session });

    client.onMessage((message) => {
      const { author, body, createdAt, fingerprint } = message || {};
      if (fingerprint !== client?.fingerprint || author !== 'human') return;
      setMessages((prev) => [...prev, { synapse: true, text: body, timestamp: createdAt }]);
    });

    client.connect();
  }, [activeHotelId, id, inputMode, session, visible]);

  const handleInput = (input, shortcutMessage = false) => {
    const nextMessages = [...messages, { text: input, timestamp: new Date().getTime() }];
    setMessages(nextMessages);

    client?.send({ body: input });

    setTimeout(async () => {
      if (client?.aiBlocked) return;
      setBusy(true);

      const context = { calendar, currency, isMobile, locale, occupation, origin, shortcutMessage };
      if (type === TYPE.CHAIN && !hotelId) context.chain = { id };
      if (hotelId) context.hotel = { id: hotelId };
      else if (hotel.id) context.hotel = hotel;

      const response = await ServiceSynapse.message({
        context,
        input,
        session,
        mcp: false,
      }).catch(() => {});

      if (response) {
        setMessages([...nextMessages, { synapse: true, ...response }]);
        client?.send({ body: response.text, author: 'ai' });

        // ! TODO: Handle locale
        // if (response.locale !== locale) Event.publish(EVENT.INTENT_LOCALE, { locale: response.locale });
        audioNotification();
      }
      setBusy(false);
    }, DELAY_RESPONSE);
  };

  const handleInputMode = (next) => {
    Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.SARAI_INTERACTION, action: 'click', method: next });
    if (next === INPUT_MODE.WHATSAPP) return handleClose();
    setInputMode(next);
  };

  const handleOpen = ({ source = SARAI_SOURCE } = {}) => {
    setVisible(true);
    Event.publish(EVENT.CHAT_OPENED, { event: EVENT.CHAT_OPENED, source });
    Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.SARAI_INTERACTION, action: 'open', method: inputMode });
  };

  const handleClose = () => {
    Event.publish(EVENT.CHAT_CLOSE, { inputMode });
    Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.SARAI_INTERACTION, action: 'close', method: inputMode });
    setVisible(false);
    setInputMode();
  };

  const isSaraiForced = urlParams.sarai === 'true';
  const isReady =
    !skeleton &&
    Object.keys(components).every((item) => !BANNED_COMPONENTS.includes(item)) &&
    (channelSettings?.web?.active || isSaraiForced);
  const { entities = {} } = messages[messages.length - 1] || {};

  const { name: privacyName, url: privacyUrl } = getPrivacyPolicy({
    chainId: id,
    features: hotel.features,
    genericName: translate(L10N.LABEL_GENERIC_NAME),
    hotelId: activeHotelId,
    isChain: type === TYPE.CHAIN,
    language,
    locale,
  });

  return isReady ? (
    <>
      <Modal {...others} fit portal visible={visible} className={styles(style.container, others.className)}>
        <Header settings={channelSettings} onClose={handleClose} />

        {!inputMode ? (
          <Welcome settings={channelSettings} onInputMode={handleInputMode} />
        ) : inputMode === INPUT_MODE.TEXT ? (
          <>
            <ScrollView scrollTo={new Date().getTime()} snap={false} className={style.messages}>
              {messages.map((message, index) => (
                <Message key={`message:${index}`} disabled={busy || index < messages.length - 1} {...message} />
              ))}
              {busy && <Message busy synapse />}
            </ScrollView>
            <Input disabled={busy} entities={entities} onValue={handleInput} className={style.input} />
          </>
        ) : inputMode === INPUT_MODE.AUDIO ? (
          <Aura
            hotelId={activeHotelId}
            settings={channelSettings}
            onCallEnd={() => setInputMode(INPUT_MODE.RATING)}
            onTranscript={({ author, body }) => {
              if (!client?.send({ author, body, meta: { audio: true } })) {
                Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: 'transcript_dropped' });
              }
            }}
          />
        ) : inputMode === INPUT_MODE.RATING ? (
          <Rating onRejoin={() => setInputMode(INPUT_MODE.AUDIO)} onReturnHome={handleClose} />
        ) : null}
        <Text light tiny className={style.warning}>
          {translate(L10N.LABEL_PRIVACY_DISCLAIMER, {
            name: privacyName,
            link: (
              <Action href={privacyUrl} target="_blank" tiny underline className={style.privacyLink}>
                {translate(L10N.ACTION_MORE_INFO)}
              </Action>
            ),
          })}
        </Text>
      </Modal>

      <ButtonDispatcher settings={channelSettings} visible={visible} onPress={handleOpen} />
    </>
  ) : null;
};

Chat.propTypes = {
  hotelId: PropTypes.string,
};

export { Chat };
